// Auth Provider centralizado del Dashboard JMA AFAC.
//
// Encapsula el ciclo de vida de MSAL.js v3:
//   - Inicialización del PublicClientApplication.
//   - Procesamiento del callback tras volver de login.microsoftonline.com
//     (handleRedirectPromise).
//   - Adquisición silenciosa de access tokens para Microsoft Graph
//     (acquireTokenSilent con fallback a redirect cuando MSAL lo exige).
//   - Logout con redirect.
//
// La validación contra la lista blanca de correos vive en allowed-users.js;
// aquí solo se exponen helpers (`isAuthorized`, `getUserEmail`).

import {
  PublicClientApplication,
  InteractionRequiredAuthError
} from '@azure/msal-browser'

import { msalConfig, loginRequest } from './msal-config.js'
import { isUserAllowed } from './allowed-users.js'

// Singleton MSAL — un único PublicClientApplication para toda la SPA.
export const msalInstance = new PublicClientApplication(msalConfig)

let initialized = false

// ---------------------------------------------------------------------------
// Modo E2E: usado por los tests de Playwright para saltarse el flujo MSAL
// real (que requeriría tokens válidos de Microsoft) sin tocar producción.
// Solo se activa cuando se cumplen DOS condiciones:
//   1. La build está en modo dev (`import.meta.env.DEV === true`).
//   2. La URL incluye el query string `?e2e=1`.
// En producción NUNCA se activa.
// ---------------------------------------------------------------------------
const E2E_MODE =
  typeof window !== 'undefined' &&
  // eslint-disable-next-line no-undef
  import.meta.env?.DEV === true &&
  new URLSearchParams(window.location.search).has('e2e')

const E2E_ACCOUNT = E2E_MODE
  ? {
      username: 'flazzarini@jmacybersec.com',
      name: 'Franco Lazzarini (E2E)',
      homeAccountId: 'e2e-fake-account',
      environment: 'login.microsoftonline.com',
      tenantId: 'e2e-tenant',
      localAccountId: 'e2e-local'
    }
  : null

if (E2E_MODE) {
  // eslint-disable-next-line no-console
  console.warn('[auth-provider] Modo E2E activo. MSAL real omitido.')
}

// ----------------------------------------------------------------------
// Mini event emitter para que las vistas reaccionen a cambios de sesión
// (login, logout local) sin acoplarse al ciclo de vida MSAL directamente.
// ----------------------------------------------------------------------
const authChangeListeners = new Set()

/**
 * Suscribe un callback a los cambios de sesión. Devuelve la función de
 * cancelación de suscripción.
 *
 * @param {() => void} callback
 * @returns {() => void}
 */
export function subscribeAuthChange(callback) {
  authChangeListeners.add(callback)
  return () => authChangeListeners.delete(callback)
}

function notifyAuthChange() {
  for (const cb of authChangeListeners) {
    try {
      cb()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[auth-provider] listener falló:', err)
    }
  }
}

/**
 * Inicializa MSAL y procesa el callback de redirect, si aplica.
 * Debe llamarse una sola vez al arranque de la app, antes del primer render
 * que dependa de la sesión.
 *
 * @returns {Promise<import('@azure/msal-browser').AccountInfo|null>} Cuenta
 * activa tras la inicialización, o null si no hay sesión.
 */
export async function initAuth() {
  if (E2E_MODE) {
    initialized = true
    return E2E_ACCOUNT
  }
  if (!initialized) {
    await msalInstance.initialize()
    initialized = true
  }

  // Si veníamos de un redirect (loginRedirect / acquireTokenRedirect),
  // MSAL resuelve aquí. Si no, retorna null sin error.
  const response = await msalInstance.handleRedirectPromise()
  if (response?.account) {
    msalInstance.setActiveAccount(response.account)
    return response.account
  }

  const accounts = msalInstance.getAllAccounts()
  if (accounts.length > 0) {
    if (!msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0])
    }
    return msalInstance.getActiveAccount()
  }

  return null
}

export function getActiveAccount() {
  if (E2E_MODE) return E2E_ACCOUNT
  return msalInstance.getActiveAccount()
}

/**
 * Devuelve el correo (UPN) de la cuenta activa o de la cuenta indicada.
 * @param {import('@azure/msal-browser').AccountInfo|null} [account]
 * @returns {string|null}
 */
export function getUserEmail(account = getActiveAccount()) {
  if (!account) return null
  return (
    account.username ||
    account.idTokenClaims?.preferred_username ||
    null
  )
}

/**
 * Determina si la cuenta indicada (o la activa) está en la lista blanca.
 * @param {import('@azure/msal-browser').AccountInfo|null} [account]
 * @returns {boolean}
 */
export function isAuthorized(account = getActiveAccount()) {
  return isUserAllowed(getUserEmail(account))
}

/**
 * Inicia el flujo de login con redirect.
 * Usamos redirect (no popup) por compatibilidad con políticas corporativas
 * de Microsoft Entra y para evitar bloqueadores de ventanas emergentes.
 */
export function login() {
  if (E2E_MODE) {
    notifyAuthChange()
    return Promise.resolve()
  }
  return msalInstance.loginRedirect(loginRequest)
}

/**
 * Cierra la sesión SOLO en el dashboard (local sign-out).
 *
 * Limpia el cache MSAL del navegador (sessionStorage) y desactiva la
 * cuenta activa, pero NO redirige a login.microsoftonline.com ni cierra
 * la sesión global del usuario en Microsoft 365. El siguiente intento
 * de "Acceder con Microsoft" reutilizará la cookie SSO de Microsoft y
 * autenticará sin pedir credenciales otra vez.
 *
 * Esta decisión responde a que el dashboard es una herramienta interna
 * usada por usuarios ya autenticados en M365 (Outlook, Teams, SharePoint).
 * Cerrar la sesión global desde aquí resultaba demasiado intrusivo.
 */
export function logout() {
  if (E2E_MODE) {
    // En E2E el logout es no-op: los tests vuelven a empezar con la URL
    // limpia o reusan el mismo state.
    notifyAuthChange()
    return
  }
  msalInstance.setActiveAccount(null)
  msalInstance.clearCache()
  notifyAuthChange()
}

/**
 * Adquiere un access token para los scopes indicados.
 * Estrategia silenciosa primero; si MSAL requiere interacción, cae a
 * acquireTokenRedirect (el flujo se reanuda en el próximo initAuth).
 *
 * @param {string[]} [scopes] Lista de scopes Graph. Por defecto los de
 *   loginRequest (User.Read, Sites.Read.All, Files.ReadWrite.All).
 * @returns {Promise<string|null>} Access token, o null si se disparó un
 *   redirect (el control sale de la página).
 */
export async function acquireToken(scopes = loginRequest.scopes) {
  if (E2E_MODE) return 'e2e-fake-token'
  const account = getActiveAccount()
  if (!account) {
    throw new Error(
      '[auth-provider] No hay cuenta activa para adquirir token.'
    )
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ account, scopes })
    return result.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes })
      return null
    }
    throw err
  }
}
